import { Check, Hash, PenLine } from "lucide-react";
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { MessageSection, PingKind } from "../../../shared/types";
import type { SaveState } from "../../components/AppStates";
import { IconButton, LoadingRaccoon } from "../../components/primitives";
import { SlackIcon } from "../../components/slack-icons";
import { emojiUrlResolver, favoriteCustomEmojiSet, favoriteEmojiItems, useEmojiCatalog } from "../../emoji";
import { standardEmojiByName, standardEmojiMatches } from "../../standard-emoji";
import { Autocomplete, type SuggestionAnchor } from "./Autocomplete";
import { EmojiPicker } from "./EmojiPicker";
import { emojiTriggerAt, type FormatAction, mentionTriggerAt } from "./editor-types";
import {
  EMPTY_FORMATS,
  type FormatMode,
  type FormatState,
  FormatToolbar,
  supportsEmoji,
  supportsFormat,
  supportsMentions,
} from "./FormatToolbar";
import { InsertMenu } from "./InsertMenu";
import { LinkPopover } from "./LinkPopover";
import { type EmojiValue, SlackEditor, type SlackEditorRange, slackEditorHandle } from "./SlackEditor";

type FieldTarget = HTMLInputElement | HTMLTextAreaElement;

function writeTextField(
  target: HTMLInputElement | HTMLTextAreaElement,
  replacement: string,
  start = target.selectionStart ?? target.value.length,
  end = target.selectionEnd ?? start,
  selectionOffset = replacement.length,
) {
  const next = `${target.value.slice(0, start)}${replacement}${target.value.slice(end)}`;
  const prototype = target instanceof HTMLTextAreaElement ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype;
  Object.getOwnPropertyDescriptor(prototype, "value")?.set?.call(target, next);
  target.dispatchEvent(new Event("input", { bubbles: true }));
  requestAnimationFrame(() => {
    target.focus();
    const cursor = start + selectionOffset;
    target.setSelectionRange(cursor, cursor);
  });
}

export function SlackField({
  value,
  onChange,
  mode,
  multiline = false,
  ...props
}: {
  value: string;
  onChange: (value: string) => void;
  mode: "rich" | "mrkdwn" | "plain";
  className?: string;
  id?: string;
  placeholder?: string;
  ariaLabel: string;
  maxLength: number;
  multiline?: boolean;
}) {
  return (
    <SlackEditor
      value={value}
      onChange={onChange}
      mode={mode === "plain" ? "emoji" : mode}
      multiline={multiline}
      {...props}
    />
  );
}

export function TextEditor({
  section,
  ping,
  primary,
  onChange,
  placeholder = "Write a message…",
  onAttach,
  onAddText,
  onAddActions,
  onAddVote,
  onAddDivider,
  onAddHeading,
  onAddContext,
  onAddTable,
  onAddMarkdown,
  onSend,
  onPreview,
  sending,
  uploading,
  sent,
  editingMessage,
  saveState,
  unfurlLinks,
  onToggleUnfurlLinks,
  stats,
  hasHeading,
  needsPingPermission,
  checkingPing,
  afterEditor,
}: {
  section: Extract<MessageSection, { type: "text" }>;
  ping: PingKind;
  primary: boolean;
  onChange: (section: MessageSection) => void;
  placeholder?: string;
  onAttach: () => void;
  onAddText: () => void;
  onAddActions: () => void;
  onAddVote: () => void;
  onAddDivider: () => void;
  onAddHeading: () => void;
  onAddContext: () => void;
  onAddTable: () => void;
  onAddMarkdown: () => void;
  onSend: () => void;
  onPreview: () => void;
  sending: boolean;
  uploading: boolean;
  sent: boolean;
  editingMessage: boolean;
  saveState: SaveState;
  unfurlLinks: boolean;
  onToggleUnfurlLinks: () => void;
  stats: { richText: number; markdown: number; blocks: number; media: number };
  hasHeading: boolean;
  needsPingPermission: boolean;
  checkingPing: boolean;
  afterEditor?: ReactNode;
}) {
  const editorRef = useRef<HTMLDivElement>(null);
  const activeRootRef = useRef<HTMLDivElement | null>(null);
  const activeFieldRef = useRef<FieldTarget | null>(null);
  const formatModeRef = useRef<FormatMode>("rich");
  const [openSurface, setOpenSurface] = useState<"emoji" | "link" | "attach" | null>(null);
  const [formattingVisible, setFormattingVisible] = useState(true);
  const [formatMode, setFormatMode] = useState<FormatMode>("rich");
  const [autocomplete, setAutocomplete] = useState<ReturnType<typeof mentionTriggerAt>>(null);
  const [emojiAutocomplete, setEmojiAutocomplete] = useState<ReturnType<typeof emojiTriggerAt>>(null);
  const [emojiSelected, setEmojiSelected] = useState(0);
  const [suggestionAnchor, setSuggestionAnchor] = useState<SuggestionAnchor | null>(null);
  const [linkSelectedText, setLinkSelectedText] = useState("");
  const [formatState, setFormatState] = useState<FormatState>(EMPTY_FORMATS);
  const { catalog: emojiCatalog } = useEmojiCatalog();
  const linkRangeRef = useRef<SlackEditorRange | null>(null);
  const linkRootRef = useRef<HTMLDivElement | null>(null);
  const emojiRangeRef = useRef<SlackEditorRange | null>(null);
  const emojiRootRef = useRef<HTMLDivElement | null>(null);
  const linkFieldRef = useRef<{
    element: FieldTarget;
    start: number;
    end: number;
    mode: FormatMode;
  } | null>(null);
  const emojiFieldRef = useRef<{ element: FieldTarget; start: number; end: number } | null>(null);

  const emojiMatches = useMemo(() => {
    const query = emojiAutocomplete?.query.toLowerCase() ?? "";
    const standard: Array<EmojiValue & { detail: string }> = standardEmojiMatches(query, 8).map((emoji) => ({
      ...emoji,
      detail: "Emoji",
    }));
    const favorites: Array<EmojiValue & { detail: string }> = favoriteEmojiItems(emojiCatalog, query).map(
      ([name, url]) => ({
        name,
        url,
        detail: "Workspace emoji",
      }),
    );
    const custom: Array<EmojiValue & { detail: string }> = Object.entries(emojiCatalog)
      .filter(([name, url]) => !favoriteCustomEmojiSet.has(name) && !url.startsWith("alias:") && name.includes(query))
      .map(([name, url]) => ({ name, url, detail: "Workspace emoji" }));
    return [...standard, ...favorites, ...custom]
      .filter((emoji, index, items) => items.findIndex((item) => item.name === emoji.name) === index)
      .slice(0, 8);
  }, [emojiAutocomplete, emojiCatalog]);

  useEffect(() => {
    if (emojiAutocomplete) setEmojiSelected(0);
  }, [emojiAutocomplete]);

  useEffect(() => {
    if (!primary) return;
    const root = activeRootRef.current;
    if (!root) return;
    const suggestionId = autocomplete
      ? "composer-entity-suggestions"
      : emojiAutocomplete
        ? "composer-emoji-suggestions"
        : null;
    root.setAttribute("aria-expanded", String(Boolean(suggestionId)));
    root.setAttribute("aria-autocomplete", "list");
    if (suggestionId) root.setAttribute("aria-controls", suggestionId);
    else root.removeAttribute("aria-controls");
  }, [autocomplete, emojiAutocomplete, primary]);

  const dismissFloatingSurface = () => {
    setOpenSurface(null);
    setAutocomplete(null);
    setEmojiAutocomplete(null);
    linkRangeRef.current = null;
    linkRootRef.current = null;
    emojiRangeRef.current = null;
    emojiRootRef.current = null;
  };

  const updateSuggestions = useCallback(() => {
    const root = activeRootRef.current;
    const mode = formatModeRef.current;
    const editor = slackEditorHandle(root);
    if (!editor) {
      setAutocomplete(null);
      setEmojiAutocomplete(null);
      return;
    }
    const before = editor.getTextBeforeCursor();
    const nextAutocomplete = supportsMentions(mode) ? mentionTriggerAt(before) : null;
    const nextEmojiAutocomplete = supportsEmoji(mode) ? emojiTriggerAt(before) : null;
    setAutocomplete(nextAutocomplete);
    setEmojiAutocomplete(nextEmojiAutocomplete);
    if (nextAutocomplete || nextEmojiAutocomplete) {
      const bounds = editor.getBounds();
      if (bounds) {
        const width = Math.min(288, window.innerWidth - 16);
        setSuggestionAnchor({
          left: Math.max(8, Math.min(bounds.left, window.innerWidth - width - 8)),
          bottom: Math.max(8, window.innerHeight - bounds.top + 5),
          maxHeight: Math.max(40, Math.min(238, bounds.top - 12)),
        });
      }
    }
  }, []);

  const updateFormatState = useCallback(() => {
    const root = activeRootRef.current;
    const mode = formatModeRef.current;
    const editor = slackEditorHandle(root);
    if (!editor || document.activeElement !== root || mode === "emoji" || mode === "none") {
      setFormatState(EMPTY_FORMATS);
      return;
    }
    const format = editor.getFormats();
    const active = (value: unknown) => Boolean(value);
    const partial = (value: unknown) => Array.isArray(value);
    const next: FormatState = {
      bold: active(format.bold),
      italic: active(format.italic),
      underline: active(format.underline),
      strike: active(format.strike),
      code: active(format.code),
      codeBlock: active(format["code-block"]),
      quote: active(format.blockquote),
      bullet: format.list === "bullet" || (Array.isArray(format.list) && format.list.includes("bullet")),
      ordered: format.list === "ordered" || (Array.isArray(format.list) && format.list.includes("ordered")),
      link: active(format.link),
      partial: {
        bold: partial(format.bold),
        italic: partial(format.italic),
        underline: partial(format.underline),
        strike: partial(format.strike),
      },
    };
    setFormatState((current) => (JSON.stringify(current) === JSON.stringify(next) ? current : next));
  }, []);

  const replaceQueryWithEmoji = useCallback((length: number, emoji: EmojiValue) => {
    slackEditorHandle(activeRootRef.current)?.replaceBeforeCursorWithEmoji(length, emoji);
  }, []);

  const updateSection = (mrkdwn: string) => {
    onChange({ ...section, mrkdwn });
    updateSuggestions();
  };

  useEffect(() => {
    if (!primary) return;
    const rememberTarget = (event: FocusEvent) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      const editor = target.closest<HTMLDivElement>("[data-mrkdwn-editor]");
      if (editor) {
        activeRootRef.current = editor;
        activeFieldRef.current = null;
        const mode = (editor.dataset.mrkdwnMode as FormatMode | undefined) ?? "rich";
        formatModeRef.current = mode;
        setFormatMode(mode);
        updateSuggestions();
        return;
      }
      const field = target.closest<HTMLInputElement | HTMLTextAreaElement>("input, textarea");
      const select = target.closest<HTMLSelectElement>("select");
      if (field || select) {
        activeRootRef.current = null;
        activeFieldRef.current = field ?? null;
        const mode = field?.dataset.formatMode === "markdown" ? "markdown" : "none";
        formatModeRef.current = mode;
        setFormatMode(mode);
        setAutocomplete(null);
        setEmojiAutocomplete(null);
        return;
      }
      if (!target.closest(".popover, .autocomplete")) {
        activeRootRef.current = null;
        activeFieldRef.current = null;
        formatModeRef.current = "none";
        setFormatMode("none");
        setAutocomplete(null);
        setEmojiAutocomplete(null);
      }
    };
    const refresh = () => {
      if (activeRootRef.current) updateSuggestions();
      updateFormatState();
    };
    const onSelectionChange = () => {
      updateFormatState();
      if (activeRootRef.current) updateSuggestions();
    };
    document.addEventListener("focusin", rememberTarget);
    document.addEventListener("input", refresh);
    document.addEventListener("keyup", refresh);
    document.addEventListener("click", refresh);
    document.addEventListener("selectionchange", onSelectionChange);
    return () => {
      document.removeEventListener("focusin", rememberTarget);
      document.removeEventListener("input", refresh);
      document.removeEventListener("keyup", refresh);
      document.removeEventListener("click", refresh);
      document.removeEventListener("selectionchange", onSelectionChange);
    };
  }, [primary, updateFormatState, updateSuggestions]);

  useEffect(() => {
    if (!primary || !emojiAutocomplete || !emojiMatches.length) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey) return;
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setEmojiSelected((current) => Math.min(current + 1, emojiMatches.length - 1));
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        setEmojiSelected((current) => Math.max(0, current - 1));
      } else if (event.key === "Tab" || event.key === "Enter") {
        const emoji = emojiMatches[emojiSelected];
        if (!emoji) return;
        event.preventDefault();
        replaceQueryWithEmoji(emojiAutocomplete.length, emoji);
        setEmojiAutocomplete(null);
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [primary, emojiAutocomplete, emojiMatches, emojiSelected, replaceQueryWithEmoji]);

  useEffect(() => {
    if (!primary || (!autocomplete && !emojiAutocomplete)) return;
    const reposition = () => {
      const root = activeRootRef.current;
      const bounds = slackEditorHandle(root)?.getBounds();
      if (!bounds) return;
      const width = Math.min(288, window.innerWidth - 16);
      setSuggestionAnchor({
        left: Math.max(8, Math.min(bounds.left, window.innerWidth - width - 8)),
        bottom: Math.max(8, window.innerHeight - bounds.top + 5),
        maxHeight: Math.max(40, Math.min(238, bounds.top - 12)),
      });
    };
    window.addEventListener("resize", reposition);
    window.addEventListener("scroll", reposition, true);
    return () => {
      window.removeEventListener("resize", reposition);
      window.removeEventListener("scroll", reposition, true);
    };
  }, [primary, autocomplete, emojiAutocomplete]);

  const formatRichEditor = (action: FormatAction) => {
    // Like Slack, applying a format focuses the composer even if the user
    // clicked the toolbar first.
    const root = activeRootRef.current ?? editorRef.current;
    if (!root) return;
    slackEditorHandle(root)?.format(action);
  };

  const formatNativeField = (action: FormatAction) => {
    const target = activeFieldRef.current;
    if (!target || !supportsFormat(formatMode, action)) return;
    const start = target.selectionStart ?? target.value.length;
    const end = target.selectionEnd ?? start;
    const selected = target.value.slice(start, end);
    const markdownBold = formatMode === "markdown";
    const [before, after] = (() => {
      if (action === "bold") return markdownBold ? ["**", "**"] : ["*", "*"];
      if (action === "italic") return ["_", "_"];
      if (action === "underline") return ["__", "__"];
      if (action === "strike") return formatMode === "markdown" ? ["~~", "~~"] : ["~", "~"];
      if (action === "code") return ["`", "`"];
      if (action === "codeBlock") return ["```\n", "\n```"];
      return ["", ""];
    })();
    if (before || after) {
      const replacement = `${before}${selected}${after}`;
      writeTextField(target, replacement, start, end, selected ? replacement.length : before.length);
      return;
    }
    const lines = (selected || "Text").split("\n");
    const replacement = lines
      .map((line, index) => `${action === "number" ? `${index + 1}. ` : action === "bullet" ? "- " : "> "}${line}`)
      .join("\n");
    writeTextField(target, replacement, start, end);
  };

  const applyFormat = (action: FormatAction) => {
    if (formatMode === "markdown" || formatMode === "none") return formatNativeField(action);
    formatRichEditor(action);
  };

  const rememberSelection = () => {
    const root = activeRootRef.current;
    if (root) {
      const editor = slackEditorHandle(root);
      if (!editor) return null;
      return {
        root,
        offsets: editor.getRange(),
        selectedText: editor.getSelectedText(),
      };
    }
    const field = activeFieldRef.current;
    if (field) {
      return {
        root: null,
        field,
        offsets: null,
        selectedText: field.value.slice(field.selectionStart ?? 0, field.selectionEnd ?? 0),
      };
    }
    return null;
  };

  const openLink = () => {
    const saved = rememberSelection();
    if (!saved) return;
    if (saved.root) {
      linkRootRef.current = saved.root;
      linkRangeRef.current = saved.offsets;
      linkFieldRef.current = null;
      setLinkSelectedText(saved.selectedText);
    } else if (saved.field) {
      const start = saved.field.selectionStart ?? saved.field.value.length;
      const end = saved.field.selectionEnd ?? start;
      linkFieldRef.current = { element: saved.field, start, end, mode: formatModeRef.current };
      linkRootRef.current = null;
      setLinkSelectedText(saved.field.value.slice(start, end));
    }
    setAutocomplete(null);
    setEmojiAutocomplete(null);
    setOpenSurface("link");
  };

  const insertLink = (label: string, url: string) => {
    const savedField = linkFieldRef.current;
    if (savedField) {
      const formatted = savedField.mode === "markdown" ? `[${label}](${url})` : `<${url}|${label}>`;
      writeTextField(savedField.element, formatted, savedField.start, savedField.end);
      linkFieldRef.current = null;
      setOpenSurface(null);
      return;
    }
    const root = linkRootRef.current ?? activeRootRef.current;
    if (!root) return;
    slackEditorHandle(root)?.insertLink(label, url, linkRangeRef.current);
    linkRangeRef.current = null;
    linkRootRef.current = null;
    setOpenSurface(null);
  };

  const rememberEmojiTarget = () => {
    const saved = rememberSelection();
    if (saved?.root) {
      emojiRootRef.current = saved.root;
      emojiRangeRef.current = saved.offsets;
      emojiFieldRef.current = null;
    } else if (saved?.field) {
      emojiFieldRef.current = {
        element: saved.field,
        start: saved.field.selectionStart ?? saved.field.value.length,
        end: saved.field.selectionEnd ?? saved.field.value.length,
      };
      emojiRootRef.current = null;
    } else {
      emojiRangeRef.current = null;
      emojiRootRef.current = null;
    }
  };

  const insertEmoji = (name: string) => {
    const savedField = emojiFieldRef.current;
    if (savedField) {
      writeTextField(savedField.element, `:${name}:`, savedField.start, savedField.end);
      return true;
    }
    const root = emojiRootRef.current ?? activeRootRef.current;
    if (!root) return false;
    const editor = slackEditorHandle(root);
    if (!editor) return false;
    editor.restoreRange(emojiRangeRef.current);
    const url = emojiUrlResolver(emojiCatalog)(name);
    if (url) editor.insertEmoji({ name, url });
    else {
      const standard = standardEmojiByName(name);
      if (standard) editor.insertEmoji(standard);
      else editor.insertText(`:${name}: `);
    }
    return true;
  };

  const insertMentionTrigger = (trigger: "@" | "#") => {
    const root = activeRootRef.current;
    if (root && (formatMode === "rich" || formatMode === "mrkdwn")) {
      slackEditorHandle(root)?.insertMentionTrigger(trigger);
      return;
    }
    const field = activeFieldRef.current;
    if (field) writeTextField(field, trigger);
  };

  const sendDisabled =
    sending ||
    checkingPing ||
    uploading ||
    sent ||
    saveState === "conflict" ||
    stats.richText > 40_000 ||
    stats.blocks > 50;

  return (
    <div className="text-editor-wrap">
      {primary && formattingVisible && (
        <FormatToolbar
          onLink={openLink}
          onFormat={applyFormat}
          onToggleUnfurls={onToggleUnfurlLinks}
          unfurlLinks={unfurlLinks}
          mode={formatMode}
          formats={formatState}
        />
      )}
      <SlackEditor
        ref={editorRef}
        className={primary ? "rich-composer" : ""}
        value={section.mrkdwn}
        onChange={updateSection}
        mode="rich"
        multiline
        ping={ping}
        maxLength={40_000}
        ariaLabel="Message text"
        placeholder={placeholder}
        submitOnEnter={primary}
        interceptEnter={primary && Boolean(autocomplete || emojiAutocomplete)}
        onSubmit={() => {
          if (!sendDisabled) onSend();
        }}
        onKeyDown={(event) => {
          if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
            event.preventDefault();
            if (!sendDisabled) onSend();
          } else if ((event.metaKey || event.ctrlKey) && event.shiftKey && event.key.toLowerCase() === "u") {
            event.preventDefault();
            openLink();
          } else if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
            // Slack's onCmdKWhenSelected: open the link form when text is selected.
            const selection = slackEditorHandle(activeRootRef.current)?.getRange();
            if (selection?.length) {
              event.preventDefault();
              openLink();
            }
          } else if (event.key === "Escape") {
            dismissFloatingSurface();
          }
        }}
      />
      {afterEditor}
      {primary && (
        <div className="composer-footer" role="toolbar" aria-label="Composer actions">
          <div className="composer-footer-prefix">
            <IconButton
              label="Attach and insert"
              onClick={() => {
                setAutocomplete(null);
                setEmojiAutocomplete(null);
                setOpenSurface((value) => (value === "attach" ? null : "attach"));
              }}
            >
              <SlackIcon name="plus" size={18} />
            </IconButton>
          </div>
          <div className="composer-footer-actions">
            <IconButton
              label={formattingVisible ? "Hide formatting" : "Show formatting"}
              active={formattingVisible}
              onClick={() => setFormattingVisible((value) => !value)}
            >
              <SlackIcon name="formatting" size={18} />
            </IconButton>
            <IconButton
              label="Emoji"
              disabled={!supportsEmoji(formatMode)}
              onClick={() => {
                rememberEmojiTarget();
                setAutocomplete(null);
                setEmojiAutocomplete(null);
                setOpenSurface((value) => (value === "emoji" ? null : "emoji"));
              }}
            >
              <span className="emoji-swap">
                <SlackIcon name="emoji" size={18} />
                <SlackIcon name="emojiFilled" size={18} />
              </span>
            </IconButton>
            <IconButton
              label={
                supportsMentions(formatMode) ? "Mention a person" : "Person mentions aren’t supported in this field"
              }
              disabled={!supportsMentions(formatMode)}
              onClick={() => insertMentionTrigger("@")}
            >
              <SlackIcon name="mentions" size={18} />
            </IconButton>
            <IconButton
              label={
                supportsMentions(formatMode) ? "Mention a channel" : "Channel mentions aren’t supported in this field"
              }
              disabled={!supportsMentions(formatMode)}
              onClick={() => insertMentionTrigger("#")}
            >
              <Hash size={18} />
            </IconButton>
          </div>
          <span className="composer-shortcut-hint">
            <kbd>Enter</kbd> send <span>·</span> <kbd>Shift</kbd> + <kbd>Enter</kbd> new line
          </span>
          <div className="composer-limit" aria-live="polite">
            <span className={stats.richText > 36_000 ? "limit-near" : ""}>
              {stats.richText.toLocaleString()} / 40,000
            </span>
            {(stats.blocks > 45 || stats.markdown > 10_800) && (
              <small className="limit-near">
                {stats.blocks} / 50 blocks
                {stats.markdown ? ` · ${stats.markdown.toLocaleString()} / 12,000 markdown` : ""}
              </small>
            )}
          </div>
          <button type="button" className="preview-button" onClick={onPreview}>
            Preview
          </button>
          <button
            type="button"
            className="send-button"
            disabled={sendDisabled}
            onClick={onSend}
            aria-label={
              sending
                ? "Sending message…"
                : checkingPing
                  ? "Checking ping permission…"
                  : editingMessage
                    ? "Edit message"
                    : needsPingPermission
                      ? "Request send"
                      : "Send message"
            }
            title={
              sending
                ? "Sending message…"
                : checkingPing
                  ? "Checking ping permission…"
                  : editingMessage
                    ? "Edit message"
                    : needsPingPermission
                      ? "Request send"
                      : "Send message"
            }
          >
            {sending || checkingPing ? (
              <LoadingRaccoon size={20} />
            ) : sent ? (
              <Check size={20} />
            ) : editingMessage ? (
              <PenLine size={18} />
            ) : needsPingPermission && emojiUrlResolver(emojiCatalog)("ms-raising-hand") ? (
              <img
                className="inline-custom-emoji send-button-emoji"
                src={emojiUrlResolver(emojiCatalog)("ms-raising-hand") ?? ""}
                alt="Request send"
              />
            ) : (
              <SlackIcon name="send" size={20} />
            )}
            <span>
              {sending
                ? "Sending"
                : checkingPing
                  ? "Checking"
                  : editingMessage
                    ? "Update"
                    : needsPingPermission
                      ? "Request"
                      : "Send"}
            </span>
          </button>
        </div>
      )}
      {primary && autocomplete && (
        <Autocomplete
          id="composer-entity-suggestions"
          trigger={autocomplete.trigger}
          query={autocomplete.query}
          anchor={suggestionAnchor}
          onPick={(item) => {
            const editor = slackEditorHandle(activeRootRef.current);
            if (!editor) return;
            const isUser = autocomplete.trigger === "@";
            const isGroup = isUser && item.kind === "group";
            const label = `${isUser ? "@" : "#"}${item.name}`;
            editor.replaceBeforeCursorWithMention(autocomplete.length, {
              kind: isGroup ? "group" : isUser ? "user" : "channel",
              id: item.id,
              label,
            });
            setAutocomplete(null);
          }}
          onDismiss={() => setAutocomplete(null)}
        />
      )}
      {primary && emojiAutocomplete && emojiMatches.length > 0 && (
        <div
          id="composer-emoji-suggestions"
          className="autocomplete caret-autocomplete emoji-autocomplete"
          role="listbox"
          style={suggestionAnchor ?? undefined}
        >
          <div className="autocomplete-title">Emoji</div>
          {emojiMatches.map((emoji, index) => (
            <button
              type="button"
              role="option"
              aria-selected={emojiSelected === index}
              className={emojiSelected === index ? "selected" : ""}
              key={emoji.name}
              onMouseEnter={() => setEmojiSelected(index)}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => {
                if (slackEditorHandle(activeRootRef.current)) {
                  replaceQueryWithEmoji(emojiAutocomplete.length, emoji);
                  setEmojiAutocomplete(null);
                }
              }}
            >
              {"url" in emoji ? (
                <img src={emoji.url} alt="" />
              ) : (
                <span className="inline-emoji-glyph" aria-hidden="true">
                  {emoji.unicode}
                </span>
              )}
              <span>
                <strong>:{emoji.name}:</strong>
                <small>{emoji.detail}</small>
              </span>
            </button>
          ))}
        </div>
      )}
      {openSurface && (
        <button
          type="button"
          className="composer-popover-backdrop"
          aria-label="Close open composer menu"
          onClick={dismissFloatingSurface}
        />
      )}
      {openSurface === "emoji" && (
        <EmojiPicker
          onClose={dismissFloatingSurface}
          onPick={(name) => {
            insertEmoji(name);
            emojiFieldRef.current = null;
            emojiRootRef.current = null;
            emojiRangeRef.current = null;
            setOpenSurface(null);
          }}
        />
      )}
      {openSurface === "link" && (
        <LinkPopover
          selectedText={linkSelectedText}
          onClose={() => {
            linkFieldRef.current = null;
            linkRootRef.current = null;
            linkRangeRef.current = null;
            setOpenSurface(null);
          }}
          onInsert={insertLink}
        />
      )}
      {openSurface === "attach" && (
        <InsertMenu
          hasHeading={hasHeading}
          onClose={() => setOpenSurface(null)}
          onAttach={onAttach}
          onAddText={onAddText}
          onAddActions={onAddActions}
          onAddVote={onAddVote}
          onAddDivider={onAddDivider}
          onAddHeading={onAddHeading}
          onAddContext={onAddContext}
          onAddTable={onAddTable}
          onAddMarkdown={onAddMarkdown}
        />
      )}
    </div>
  );
}
